import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Loader2, Printer, Link2, ArrowLeft, Mail, MapPin, Sparkles, Send, Instagram, Facebook, Youtube, Globe, ShoppingBag } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { fetchProtocols, fetchProtocolFocus, fetchPublicDealer, renderFlyer, isAdminUser, canAccessCrm, publicCoupon } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import FlyerEmailDialog from "@/components/FlyerEmailDialog";
import { BrandLogo } from "@/components/BrandLogo";

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
const fixText = (t) => (t || "").replace(/â€™/g, "’").replace(/â€"/g, "—").replace(/Â/g, "").trim();
const stripHtml = (h) => fixText((h || "").replace(/<style[\s\S]*?<\/style>/gi, "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " "));

// Printable protocol brochure: dealer header → protocol focus → selected
// products (photo, short about, key peptides) → dealer footer.
export default function ProtocolFlyer() {
  const q = new URLSearchParams(useLocation().search);
  const dealer = q.get("d") || "orevital";
  const focusName = q.get("focus") || "";
  const ids = useMemo(() => (q.get("ids") || "").split(",").filter(Boolean), [q]);
  const [state, setState] = useState({ loading: true, products: [], dealer: null, focus: null });
  const { user } = useAuth();
  const recId = user?.user_record_id || user?.record_id;
  const canEmail = !!recId && (isAdminUser(user) || canAccessCrm(user));
  const [emailOpen, setEmailOpen] = useState(false);
  const [flyer, setFlyer] = useState(null);
  const [rendering, setRendering] = useState(false);
  const couponId = q.get("coupon") || "";
  const [coupon, setCoupon] = useState(null);
  useEffect(() => {
    if (!couponId) { setCoupon(null); return; }
    publicCoupon(couponId).then(setCoupon).catch(() => setCoupon(null));
  }, [couponId]);

  async function openEmail() {
    setRendering(true);
    try {
      const res = await renderFlyer({
        user_record_id: recId,
        type: focusName ? "protocol" : "product",
        focus_slug: focusName ? focusName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") : undefined,
        product_ids: ids,
      });
      setFlyer(res);
      setEmailOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build the email flyer");
    } finally {
      setRendering(false);
    }
  }

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [cat, pub, foc] = await Promise.all([
          fetchProtocols([]),
          fetchPublicDealer(dealer).catch(() => null),
          focusName ? fetchProtocolFocus(focusName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")).catch(() => null) : Promise.resolve(null),
        ]);
        const byId = Object.fromEntries((cat.products || []).map((p) => [p.id, p]));
        const products = ids.map((id) => byId[id]).filter(Boolean);
        if (active) setState({ loading: false, products, dealer: pub, focus: foc?.focus || null });
      } catch {
        if (active) setState((s) => ({ ...s, loading: false }));
      }
    })();
    return () => {
      active = false;
    };
  }, [dealer, focusName, ids]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Flyer link copied");
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  const { loading, products, dealer: d, focus } = state;
  const dealerName = d ? `${d.first_name || ""} ${d.last_name || ""}`.trim() : "";
  const shopUrl = `${window.location.origin}/d/${dealer}?view=shop`;

  return (
    <div className="min-h-screen bg-zinc-100 print:bg-white" data-testid="protocol-flyer-page">
      <div className="max-w-3xl mx-auto px-3 sm:px-4 py-4 flex items-center gap-2 print:hidden">
        <Link to={focus ? `/protocols/${focus.slug}?d=${dealer}` : `/d/${dealer}?view=protocols`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0b2545] hover:underline" data-testid="flyer-back">
          <ArrowLeft size={14} /> Back
        </Link>
        <div className="flex-1" />
        {canEmail && (
          <button type="button" onClick={openEmail} disabled={rendering} className="h-9 px-4 rounded-full bg-violet-600 text-white text-xs font-bold inline-flex items-center gap-1.5 hover:bg-violet-700 disabled:opacity-60" data-testid="flyer-email-btn">
            {rendering ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Email this flyer
          </button>
        )}
        <button type="button" onClick={copyLink} className="h-9 px-3 rounded-full border border-zinc-300 bg-white text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-zinc-50" data-testid="flyer-copy-link"><Link2 size={14} /> Copy link</button>
        <button type="button" onClick={() => window.print()} className="h-9 px-4 rounded-full bg-[#0b2545] text-white text-xs font-bold inline-flex items-center gap-1.5 hover:bg-[#123566]" data-testid="flyer-print"><Printer size={14} /> Print / Save PDF</button>
      </div>

      {loading ? (
        <div className="py-24 text-center text-zinc-500"><Loader2 size={18} className="animate-spin inline mr-2" />Building your flyer…</div>
      ) : (
        <article className="max-w-3xl mx-auto bg-white shadow-sm print:shadow-none rounded-3xl print:rounded-none overflow-hidden mb-10 print:mb-0" data-testid="flyer-sheet">
          {/* Dealer header */}
          <header className="px-6 sm:px-10 pt-7 pb-5 flex items-center gap-4 border-b border-zinc-100" data-testid="flyer-header">
            {d?.dealer_logo || d?.image_url ? (
              <img src={d.dealer_logo || d.image_url} alt={dealerName} className="h-16 w-16 rounded-2xl object-cover bg-zinc-50 shrink-0" onError={(e) => (e.currentTarget.style.display = "none")} />
            ) : null}
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[3px] text-[#b88a2b] font-bold">Your OreVital protocol</div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#0b2545] leading-tight">{d?.company_name || dealerName || "OreVital"}</h1>
              <div className="text-xs text-zinc-500 mt-0.5">{[dealerName && d?.company_name ? dealerName : "", d?.company, [d?.city, d?.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}</div>
            </div>
            <BrandLogo className="hidden sm:block" />
          </header>

          {/* Focus intro */}
          {focus && (
            <section className="grid sm:grid-cols-5 bg-[#0b2545] text-white" data-testid="flyer-focus">
              {focus.hero_image && <img src={focus.hero_image} alt={focus.name} className="sm:col-span-2 w-full h-44 sm:h-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />}
              <div className={`p-6 ${focus.hero_image ? "sm:col-span-3" : "sm:col-span-5"}`}>
                <div className="text-[10px] uppercase tracking-[3px] text-[#d6ad52] font-semibold inline-flex items-center gap-1"><Sparkles size={11} /> Protocol Focus</div>
                <h2 className="text-2xl font-bold mt-1">{focus.name}</h2>
                {focus.description && <p className="text-sm text-white/85 mt-2 leading-relaxed">{focus.description}</p>}
              </div>
            </section>
          )}

          {coupon?.code && (
            <section className="mx-6 sm:mx-10 mt-5 rounded-2xl border-2 border-dashed border-[#d6ad52] bg-[#fbf7ee] p-4 flex flex-wrap items-center justify-between gap-3" data-testid="flyer-coupon">
              <div>
                <div className="text-[10px] uppercase tracking-[3px] text-[#b88a2b] font-bold">Your coupon</div>
                <div className="text-sm text-zinc-700 mt-0.5">{coupon.amount ? <><b>{coupon.amount}</b> this protocol — use the code at checkout</> : "Use this code at checkout"}{coupon.tier ? ` · ${coupon.tier} pricing` : ""}{coupon.expiry ? ` · valid until ${coupon.expiry}` : ""}</div>
              </div>
              <button type="button" onClick={() => navigator.clipboard.writeText(coupon.code).then(() => toast.success("Coupon code copied"))} className="font-mono text-base font-bold tracking-widest text-[#0b2545] bg-white border border-[#d6ad52] rounded-xl px-4 py-2 hover:bg-[#fff8e6]" data-testid="flyer-coupon-code">{coupon.code}</button>
            </section>
          )}

          {/* Divider (about moved to footer) */}
          <div className="mx-6 sm:mx-10 border-t-2 border-[#d6ad52]/60" data-testid="flyer-divider" />

          {/* Products */}
          <section className="px-6 sm:px-10 py-6" data-testid="flyer-products">
            {products.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-10">No products selected. Go back and tap “Flyer” on the products you want.</p>
            ) : (
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-5 print:grid-cols-2">
                {products.map((p) => (
                  <li key={p.id} className="flex gap-3 break-inside-avoid" data-testid={`flyer-product-${p.id}`}>
                    <div className="w-24 h-24 rounded-2xl bg-zinc-50 border border-zinc-100 shrink-0 overflow-hidden">
                      {p.image_url && <img src={p.image_url} alt={p.name} className="w-full h-full object-contain p-1.5" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-sm font-bold text-zinc-900 leading-snug">{p.name}</h3>
                        <span className="text-sm font-bold text-[#0b2545] shrink-0">{fmt(p.price)}</span>
                      </div>
                      {p.excerpt && <p className="text-xs text-zinc-700 mt-1 leading-relaxed" data-testid={`flyer-product-desc-${p.id}`}>{fixText(p.excerpt)}</p>}
                      {p.concerns?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {p.concerns.slice(0, 4).map((c) => <span key={c} className="rounded-full bg-violet-50 text-violet-800 px-1.5 py-0.5 text-[9px] font-semibold">{c}</span>)}
                        </div>
                      )}
                      <a href={`/d/${dealer}?view=product&p=${encodeURIComponent(p.slug || p.id)}${coupon?.code ? `&coupon=${encodeURIComponent(coupon.code)}` : ""}`}
                        className="print:hidden inline-flex items-center gap-1 mt-2 rounded-full bg-[#0b2545] text-white px-3 py-1.5 text-[11px] font-bold hover:bg-[#143462]" data-testid={`flyer-buy-${p.id}`}>
                        <ShoppingBag size={11} /> Buy
                      </a>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Footer — premium dealer card, mirrors the email footer */}
          <footer className="bg-white border-t-[3px] border-[#d6ad52]" data-testid="flyer-footer">
            <div className="px-6 sm:px-10 py-6 flex flex-col sm:flex-row gap-5 sm:items-center">
              {d?.image_url && (
                <img src={d.image_url} alt={dealerName} className="w-20 h-20 rounded-full object-cover border-2 border-[#d6ad52] shrink-0" data-testid="flyer-footer-photo" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-lg font-bold text-[#0b2545] leading-tight">{dealerName || d?.company_name || "OreVital"}</div>
                {(d?.company_name || d?.company) && <div className="text-xs text-zinc-500 tracking-wide">{d.company_name || d.company}</div>}
                <div className="text-xs text-zinc-700 mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
                  {d?.email_id && <span className="inline-flex items-center gap-1"><Mail size={12} className="text-[#b88a2b]" /> {d.email_id}</span>}
                  {(d?.city || d?.state) && <span className="inline-flex items-center gap-1"><MapPin size={12} className="text-[#b88a2b]" /> {[d.city, d.state].filter(Boolean).join(", ")}</span>}
                  {d?.website_url && <span className="inline-flex items-center gap-1"><Globe size={12} className="text-[#b88a2b]" /> {d.website_url.replace(/^https?:\/\//, "")}</span>}
                </div>
                {d?.aboutus && (
                  <p className="text-[11px] text-zinc-600 leading-relaxed mt-2 max-w-xl" data-testid="flyer-about">
                    {stripHtml(d.aboutus).slice(0, 260)}{stripHtml(d.aboutus).length > 260 ? "…" : ""}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-3">
                  {[["instagram", d?.dealer_instagram, Instagram], ["facebook", d?.dealer_facebook, Facebook], ["youtube", d?.youtube, Youtube]].filter(([, v]) => v).map(([k, v, Icon]) => (
                    <a key={k} href={v.startsWith("http") ? v : `https://${k}.com/${v.replace(/^@/, "")}`} target="_blank" rel="noreferrer" className="h-7 w-7 rounded-full bg-[#0b2545] text-white flex items-center justify-center" data-testid={`flyer-social-${k}`}>
                      <Icon size={13} />
                    </a>
                  ))}
                  {d?.dealer_twitter && (
                    <a href={d.dealer_twitter.startsWith("http") ? d.dealer_twitter : `https://x.com/${d.dealer_twitter.replace(/^@/, "")}`} target="_blank" rel="noreferrer" className="h-7 w-7 rounded-full bg-[#0b2545] text-white flex items-center justify-center text-[11px] font-bold" data-testid="flyer-social-x">X</a>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 sm:flex-col sm:items-center shrink-0" data-testid="flyer-qr">
                <div className="p-2 rounded-xl border border-zinc-200 bg-white">
                  <QRCodeSVG value={shopUrl} size={92} fgColor="#0b2545" level="M" includeMargin={false} />
                </div>
                <div className="text-[10px] font-bold tracking-[2px] text-[#0b2545] uppercase text-center leading-tight">Scan to shop<br />with {d?.first_name || "me"}</div>
              </div>
            </div>
            <div className="bg-[#0b2545] text-center py-2.5 text-[10px] font-bold tracking-[2px] text-[#d6ad52]">OREVITAL.COM &bull; MINDFUL MINERALS</div>
          </footer>
        </article>
      )}
      {flyer && <FlyerEmailDialog open={emailOpen} onOpenChange={setEmailOpen} recId={recId} user={user} flyer={flyer} />}
    </div>
  );
}
